import type { NamedFieldProps } from "./Field.js";
import type { ImmutableArray } from "shelving";
import { ArraySchema, BooleanStore, FileSchema, assertArray, assertInstance, getSource, omitArrayItems, withArrayItems } from "shelving";
import { useInstance, useStore } from "shelving/react";
import { Icon } from "../base/Icon.js";
import { Slides } from "../base/Slides.js";
import { MediaImage } from "../media/MediaImage.js";
import { Field, useField } from "./Field.js";
import { FieldNotice } from "./FieldNotice.js";
import { FileInput } from "./FileInput.js";

/** Post media field is an interactive carousel that lets the user select/upload images and flick through them to add more. */
export function MediaSlidesField(props: { uploader: (file: File) => Promise<string> } & NamedFieldProps): React.ReactElement {
	const field = useField<ImmutableArray<string>>(props);
	const { name, form, schema, value } = field;
	useStore(form);
	useStore(form.busy);
	const busy = useStore(useInstance(BooleanStore));
	const { max, items } = getSource(ArraySchema, schema);
	assertInstance(items, FileSchema);
	const { types } = items;
	return (
		<Field {...field}>
			<Slides mods="space-none">
				{value?.map((item, i) => (
					<figure key={i}>
						<MediaImage path={item} />
						<button
							type="button"
							className="button pin-top pin-right"
							onClick={() => {
								const v = form.data[name] ?? schema.value;
								assertArray(v);
								form.publish(name, omitArrayItems(v, item));
							}}
							title="Remove image"
						>
							<Icon icon="delete" />
						</button>
					</figure>
				))}
				{(value ? value.length : 0) < max ? (
					<label key="add" className={`button ${busy.value ? "effect-waiting" : ""}`}>
						<Icon icon="add" />
						<FileInput
							types={types}
							multiple={max !== 1}
							onFiles={async (...files) => {
								busy.set(true);
								const uploads = await Promise.all(files.map(props.uploader));
								const existing = form.data[name] ?? schema.value;
								assertArray(existing);
								form.publish(name, withArrayItems(existing, ...uploads));
								busy.set(false);
							}}
							disabled={busy.value || form.busy.value}
						/>
					</label>
				) : null}
			</Slides>
			<FieldNotice name={name} />
		</Field>
	);
}
